import { useState, useCallback, useEffect } from 'react';
import { useChatLayoutStore } from './hooks/useChatLayoutStore';
import { twMerge } from 'tailwind-merge';

export function SideDrawer({
  open,
  width,
  minWidth = 200,
  children,
  header,
  zIndex = 40,
  resizable = false,
  className,
}: {
  open: boolean;
  width?: number;
  minWidth?: number;
  children: React.ReactNode;
  header?: React.ReactNode;
  zIndex?: number;
  resizable?: boolean;
  className?: string;
}) {
  const { drawerWidth, setDrawerWidth } = useChatLayoutStore();

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartX, setDragStartX] = useState<number>(0);
  const [drawerWidthStart, setDrawerWidthStart] = useState<number>(0);

  const handleDragStart = useCallback(
    (e: any) => {
      setDragStartX(e.clientX);
      setIsDragging(true);
      setDrawerWidthStart(drawerWidth);
    },
    [setDragStartX, setIsDragging, setDrawerWidthStart, drawerWidth],
  );

  const handleDragMove = useCallback(
    (e: any) => {
      if (!isDragging) {
        return;
      }
      e.preventDefault();
      const delta = dragStartX - e.clientX;
      setDrawerWidth(Math.max(minWidth, drawerWidthStart + delta));
    },
    [drawerWidthStart, dragStartX, isDragging, minWidth, setDrawerWidth],
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, [setIsDragging]);

  useEffect(() => {
    if (!resizable) {
      return;
    }
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('mousemove', handleDragMove);
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [resizable, handleDragMove, handleDragEnd]);

  return (
    <div
      className={twMerge(
        'flex flex-col fixed top-[3.5rem] right-0 z-40 h-[calc(100%-3.5rem)] rounded-md bg-neutral-0 shadow-xl border-t border-l border-neutral-200',
        className,
      )}
      style={{
        transition: 'transform 200ms ease-in-out',
        transform: open ? undefined : 'translateX(100%)',
        zIndex,
      }}
      onClick={e => e.stopPropagation()}
    >
      {open && (
        <>
          {header}
          <div className="relative p-4 overflow-y-auto w-full" style={{ width }}>
            {children}
          </div>

          {resizable && (
            <div
              className="absolute top-0 h-screen z-10 cursor-col-resize"
              style={{ left: -4, width: 8 }}
              onMouseDown={handleDragStart}
            />
          )}
        </>
      )}
    </div>
  );
}
